import { TeaserFields } from '../../components/custom/teaser';

export enum TeaserPageTypes {
  PRESS = 'press',
  BLOG = 'blog',
  STORY = 'story',
  DID = 'did',
  PRODUCT = 'product',
  DEFAULT = 'default'
}

const embedTriangle = (input = ''): string => {
  const cleaned: string = input?.replace(/<[\s\S]*?>/ig, '');
  const embeded: string = cleaned?.replace(/\[blacktriangle\]|▼/ig, '');
  return embeded;
};

const pressTeaserMappings = (article, globalSettings) => {
  const isProtected = article.content.authorized_roles?.length > 0;
  return {
    headline: embedTriangle(
      article.content.teaser_title
      || article.content.headline
      || article.name),
    description: isProtected
      ? globalSettings.protected_press_articles_text
      : article.content.teaser_text,
  };
};

const productTeaserMappings = (article) => ({
  // eslint-disable-next-line max-len
  headline: embedTriangle(article.content.teaser_title || article.content.teaser_product_title || article.name),
  description: article.content.teaser_text || article.content.teaser_product_description,
  date: null,
});

const didTeaserMappings = (article, globalSettings) => {
  // eslint-disable-next-line max-len
  const filterAuthors = (authors = []) => authors.filter(({ content }) => content?.firstname || content?.lastname);
  return {
    authors: filterAuthors(article.content.authors)
      .map(({ content: { firstname = '', lastname = '' }, uuid }) => ({
        name: `${firstname} ${lastname}`,
        url: `/${globalSettings.overviewPageUrl}?authors=${uuid}`,
        identifier: uuid,
      })),
  };
};

export const TeaserPageTypesSettings = {
  press: {
    mappings: pressTeaserMappings,
    component: 'page-press-news',
    overviewUrl: (): string => '',
    fallbackImage: (globalSettings) => globalSettings?.press_articles_teaser_fallback_image,
  },
  story: {
    mappings: null,
    component: 'aktuelles',
    overviewUrl: (): string => '',
    fallbackImage: (globalSettings) => globalSettings?.press_articles_teaser_fallback_image,
  },
  blog: {
    mappings: null,
    component: 'aktuelles',
    // eslint-disable-next-line max-len
    overviewUrl: (globalSettings): string => globalSettings?.blog_overview_page_link?.story?.fullSlug,
    fallbackImage: (globalSettings) => globalSettings?.press_articles_teaser_fallback_image,
  },
  did: {
    mappings: didTeaserMappings,
    component: 'did-article-page',
    // eslint-disable-next-line max-len
    overviewUrl: (globalSettings): string => globalSettings?.did_overview_page_link?.story?.fullSlug,
    fallbackImage: (globalSettings) => globalSettings?.press_articles_teaser_fallback_image,
  },
  product: {
    mappings: productTeaserMappings,
    component: 'page-product',
    overviewUrl: (): string => '',
    fallbackImage: (globalSettings) => globalSettings?.product_overview_teaser_fallback_image,
  },
  default: {
    mappings: null,
    overviewUrl: (): string => '',
    fallbackImage: (globalSettings) => globalSettings?.press_articles_teaser_fallback_image,
  },
};

// eslint-disable-next-line max-len
export const getTeaserMapping = (contentType: TeaserPageTypes = TeaserPageTypes.DEFAULT) => (article, teaserSettings, globalSettings): TeaserFields => {
  // eslint-disable-next-line max-len
  const { mappings, overviewUrl, fallbackImage } = TeaserPageTypesSettings[contentType] || TeaserPageTypesSettings.default;
  const overviewPageUrl = (overviewUrl(globalSettings) || '').replace(/\/$/, '');
  const teaserImage = article.content.teaser_image?.filename
    ? article.content.teaser_image
    : fallbackImage(globalSettings);

  return {
    teaser_url: {
      url: article.full_slug,
      fieldtype: 'multilink',
    },
    headline: embedTriangle(article.content.teaser_title || article.name),
    description: article.content.teaser_text,
    date: article.content.teaser_hide_date ? null : article.content.release_date,
    size: teaserSettings?.size || 'large',
    layout: teaserSettings?.layout,
    teaser_type: article?.content?.teaser_type,
    slotted_media: [{
      // eslint-disable-next-line no-underscore-dangle
      _uid: `${article.content._uid}-image`,
      component: 'roche-image',
      src: {
        ...(teaserImage || {}),
        fieldtype: 'asset',
      },
    }],
    slotted_link: teaserSettings?.link_text && teaserSettings.link_text?.length > 0 ? [{
      // eslint-disable-next-line no-underscore-dangle
      _uid: `${article.content._uid}-link`,
      component: 'roche-generic-link',
      icon: 'chevron-right',
      text: teaserSettings.link_text,
      url: {
        url: article.full_slug,
        fieldtype: 'multilink',
      },
    }] : [],
    categories: overviewPageUrl && article.content.categories?.length > 0
      ? article.content.categories.map(({ name, uuid, content }) => ({
        text: name,
        url: `/${overviewPageUrl}?categories=${uuid}`,
        identifier: uuid,
        theme: content?.theme,
      }))
      : [],
    ...(typeof mappings === 'function' ? mappings(article, { ...globalSettings, overviewPageUrl }, teaserSettings) : {}),
    invisible_date: teaserSettings.invisible_date,
  };
};
